import { Navigate, createBrowserRouter } from "react-router-dom";

import DashboardPage from "@/app/pages/dashboard";
import { OrganisationProvider } from "@/features/select-organisation/components/organisation-provider";
import * as Sentry from "@sentry/react";

import { ErrorBoundary } from "./components/error-boundary";
import { NotFound } from "./components/not-found";
import { AuthLayout } from "./layouts/auth.layout";
import { AuthenticatedGuard } from "./layouts/authenticated.guard";
import DashboardLayout from "./layouts/dashboard.layout";
import { EventGuard } from "./layouts/event-guard";
import OnboardingLayout from "./layouts/onboarding.layout";
import { PreCallLayout } from "./layouts/pre-call.layout";
import { SaOrganisationLayout } from "./layouts/sa-organisation.layout";
import { SettingsLayout } from "./layouts/settings.layout";
import { SuperAdminGuard } from "./layouts/superadmin.guard";
import { SuperAdminLayout } from "./layouts/superadmin.layout";
import { UnauthenticatedGuard } from "./layouts/unauthenticated.guard";
import { VidyoLayout } from "./layouts/vidyo.layout";
import AccountSettingsPage from "./pages/account-settings";
import { AddRoomSystem } from "./pages/admin/add-room-system";
import { AnnouncementsPage } from "./pages/admin/announcements";
import { AdminInvitationsPage } from "./pages/admin/invitations";
import { AdminEventCategoriesPage } from "./pages/admin/organisation-event-categories";
import { OrganisationLicense } from "./pages/admin/organisation-license";
import { OrganisationSessionsPage } from "./pages/admin/organisation-sessions";
import { AdminTagsPage } from "./pages/admin/organisation-tags";
import { AdminOrganizationsPage } from "./pages/admin/organisations";
import { AdminRecordings } from "./pages/admin/recordings";
import { OrganisationReporting } from "./pages/admin/reporting/organisation-reporting";
import { ReplayReporting } from "./pages/admin/reporting/replay-reporting";
import { ReportingOverview } from "./pages/admin/reporting/reporting-overview";
import { RoomSystemReporting } from "./pages/admin/reporting/room-system-reporting";
import { RoomSystem } from "./pages/admin/room-system";
import { AdminRoomSystems } from "./pages/admin/room-systems";
import { SuperAdminSessionsPage } from "./pages/admin/sessions";
import { AdminUserPage } from "./pages/admin/user";
import { AdminUsersPage } from "./pages/admin/users";
import { ViewAnnouncementPage } from "./pages/admin/view-announcement";
import { ViewOrganisation } from "./pages/admin/view-organisation";
import { ConfirmEmail } from "./pages/auth/confirm-email";
import { ForgotPassword } from "./pages/auth/forgot-password";
import { SelectMfa } from "./pages/auth/select-mfa";
import { SetNewPassword } from "./pages/auth/set-new-password";
import { SetupTotp } from "./pages/auth/setup-totp";
import { SignIn } from "./pages/auth/sign-in";
import { VerifyEmail } from "./pages/auth/verify-email";
import { VerifySMS } from "./pages/auth/verify-sms";
import { VerifyTotp } from "./pages/auth/verify-totp";
import { Welcome } from "./pages/auth/welcome";
import { YourDetails } from "./pages/auth/your-details";
import { AfterCall } from "./pages/call/after-call";
import { CallPage } from "./pages/call/call";
import { PreCall } from "./pages/call/pre-call";
import EditSessionPage from "./pages/edit-session";
import CreateEventPage from "./pages/events/create-event";
import EditEventPage from "./pages/events/edit-event";
import { EventPage } from "./pages/events/event";
import { EventsPage } from "./pages/events/events";
import ManageEventPage from "./pages/events/manage-event";
import { MyEventsPage } from "./pages/events/my-events";
import { PlaylistPage } from "./pages/playlist";
import { PlaylistsPage } from "./pages/playlists";
import { RecordingPage } from "./pages/recording";
import { RecordingsPage } from "./pages/recordings";
import { RecordingsBySessionPage } from "./pages/recordings-by-session";
import { RecordingsStoragePage } from "./pages/recordings-storage";
import ScheduleSessionPage from "./pages/schedule-session";
import SessionPage from "./pages/session";
import SessionsPage from "./pages/sessions";
import { SessionsListPage } from "./pages/sessions-list";
import { PendingInvitationsPage } from "./pages/sessions/pending-invitations";
import { SessionRoomSystem } from "./pages/sessions/room-system";
import { SessionRoomSystems } from "./pages/sessions/room-systems";
import { EventCategoriesPage } from "./pages/settings/event-categories";
import { InvitationsPage } from "./pages/settings/invitations";
import { LicensePage } from "./pages/settings/license";
import { OrganisationDetailsPage } from "./pages/settings/organisation-details";
import { ReplayPage } from "./pages/settings/replay";
import { RoomSystemsSettings } from "./pages/settings/room-systems-settings";
import { SettingsRedirection } from "./pages/settings/settings-redirection";
import { TagsPage } from "./pages/settings/tags";
import { UserPage } from "./pages/settings/user";
import { UsersPage } from "./pages/settings/users";
import { SharedRecordingPage } from "./pages/shared-recording";

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const router = sentryCreateBrowserRouter(
  [
    {
      errorElement: <ErrorBoundary />,
      element: (
        <AuthenticatedGuard>
          <OrganisationProvider>
            <DashboardLayout />
          </OrganisationProvider>
        </AuthenticatedGuard>
      ),
      children: [
        {
          path: "/",
          element: <Navigate to="/sessions" />,
        },
        {
          path: "/dashboard",
          element: <DashboardPage />,
        },
        {
          element: <EventGuard />,
          children: [
            {
              path: "/events",
              element: <Navigate to="/events/discover" />,
            },
            {
              path: "/events/discover",
              element: <EventsPage />,
            },
            {
              path: "/events/my",
              element: <MyEventsPage />,
            },
            {
              path: "/events/create",
              element: <CreateEventPage />,
            },
            {
              path: "/events/:id",
              element: <EventPage />,
            },
            {
              path: "/events/:id/edit",
              element: <EditEventPage />,
            },
            {
              path: "/events/:id/edit-attendees",
              element: <ManageEventPage />,
            },
          ],
        },
        {
          path: "/settings",
          element: <SettingsRedirection />,
        },
        {
          element: <SettingsLayout />,
          children: [
            {
              path: "/settings/details",
              element: <OrganisationDetailsPage />,
            },
            {
              path: "/settings/users",
              element: <UsersPage />,
            },
            {
              path: "/settings/users/:id",
              element: <UserPage />,
            },
            {
              path: "/settings/invitations",
              element: <InvitationsPage />,
            },
            {
              path: "/settings/licence",
              element: <LicensePage />,
            },
            {
              path: "/settings/tags",
              element: <TagsPage />,
            },
            {
              path: "/settings/event-categories",
              element: <EventCategoriesPage />,
            },
            {
              path: "/settings/replay",
              element: <ReplayPage />,
            },
            {
              path: "/settings/room-systems",
              element: <RoomSystemsSettings />,
            },
          ],
        },
        {
          path: "/account-settings",
          element: <AccountSettingsPage />,
        },
        {
          path: "/sessions",
          element: <SessionsPage />,
          children: [
            {
              index: true,
              element: <Navigate to="/sessions/all" />,
            },
            {
              path: "/sessions/all",
              element: <SessionsListPage />,
            },
            {
              path: "/sessions/my-sessions",
              element: <SessionsListPage />,
            },
            {
              path: "/sessions/room-systems",
              element: <SessionRoomSystems />,
            },
            {
              path: "/sessions/pending-invitations",
              element: <PendingInvitationsPage />,
            },
          ],
        },
        {
          path: "/sessions/room-systems/:id",
          element: <SessionRoomSystem />,
        },
        {
          path: "/sessions/:sessionId",
          element: <SessionPage />,
        },
        {
          path: "/sessions/:sessionId/edit",
          element: <EditSessionPage />,
        },
        {
          path: "/sessions/schedule",
          element: <ScheduleSessionPage />,
        },
        {
          path: "/recordings/session/:sessionId",
          element: <RecordingsBySessionPage />,
        },
        {
          path: "/recordings",
          element: <Navigate to="/recordings/all" />,
        },
        {
          path: "/recordings/storage",
          element: <RecordingsStoragePage />,
        },
        {
          path: "/recordings/all",
          element: <RecordingsPage />,
        },
        {
          path: "/recordings/playlists",
          element: <PlaylistsPage />,
        },
        {
          path: "/recordings/playlists/:id",
          element: <PlaylistPage />,
        },
        {
          path: "/recordings/:id",
          element: <RecordingPage />,
        },
        {
          element: <SuperAdminGuard />,
          children: [
            {
              element: <SuperAdminLayout />,
              children: [
                {
                  path: "/admin",
                  element: <Navigate to="/admin/organisations" />,
                },
                {
                  path: "/admin/organisations",

                  element: <AdminOrganizationsPage />,
                },
                {
                  path: "/admin/room-systems/new",
                  element: <AddRoomSystem />,
                },
                {
                  path: "/admin/room-systems/:id",
                  element: <RoomSystem />,
                },
                {
                  path: "/admin/room-systems",
                  element: <AdminRoomSystems />,
                },
                {
                  path: "/admin/users",
                  element: <AdminUsersPage />,
                },
                {
                  path: "/admin/users/:userId",
                  element: <AdminUserPage />,
                },
                {
                  path: "/admin/invitations",
                  element: <AdminInvitationsPage />,
                },
                {
                  path: "/admin/sessions",
                  element: <SuperAdminSessionsPage />,
                },
                {
                  path: "/admin/reporting/overview",
                  element: <ReportingOverview />,
                },
                {
                  path: "/admin/reporting/organisations",
                  element: <OrganisationReporting />,
                },
                {
                  path: "/admin/reporting/room-systems",
                  element: <RoomSystemReporting />,
                },
                {
                  path: "/admin/reporting/replay",
                  element: <ReplayReporting />,
                },
                {
                  path: "/admin/announcements",
                  element: <Navigate to="/admin/announcements/all" />,
                },
                {
                  path: "/admin/announcements/all",
                  element: <AnnouncementsPage />,
                },
                {
                  path: "/admin/announcements/:id",
                  element: <ViewAnnouncementPage />,
                },
                {
                  path: "/admin/recordings",
                  element: <Navigate to="/admin/recordings/all" />,
                },
                {
                  path: "/admin/recordings/all",
                  element: <AdminRecordings type="all" />,
                },
                {
                  path: "/admin/recordings/deletion-queue",
                  element: <AdminRecordings type="deletion-queue" />,
                },
              ],
            },
            {
              element: <SaOrganisationLayout />,
              children: [
                {
                  path: "/admin/organisations/:id/overview",
                  element: <ViewOrganisation />,
                },
                {
                  path: "/admin/organisations/:id/sessions",
                  element: <OrganisationSessionsPage />,
                },
                {
                  path: "/admin/organisations/:id/invitations",
                  element: <AdminInvitationsPage />,
                },
                {
                  path: "/admin/organisations/:id/users",
                  element: <AdminUsersPage />,
                },
                {
                  path: "/admin/organisations/:id/users/:userId",
                  element: <AdminUserPage />,
                },
                {
                  path: "/admin/organisations/:id/license",
                  element: <OrganisationLicense />,
                },
                {
                  path: "/admin/organisations/:id/tags",
                  element: <AdminTagsPage />,
                },
                {
                  path: "/admin/organisations/:id/event-categories",
                  element: <AdminEventCategoriesPage />,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      errorElement: <ErrorBoundary />,
      element: <OnboardingLayout />,
      children: [
        { path: "/sign-up/welcome", element: <Welcome /> },
        { path: "/sign-up/your-details", element: <YourDetails /> },
        {
          path: "/sign-up/confirm-email",
          element: (
            <UnauthenticatedGuard>
              <ConfirmEmail />
            </UnauthenticatedGuard>
          ),
        },
        {
          path: "/auth/setup-2fa",
          element: (
            <AuthenticatedGuard>
              <SetupTotp />
            </AuthenticatedGuard>
          ),
        },
      ],
    },
    {
      errorElement: <ErrorBoundary />,
      element: (
        <UnauthenticatedGuard>
          <AuthLayout />
        </UnauthenticatedGuard>
      ),
      children: [
        {
          path: "/sign-in",
          element: <SignIn />,
        },
        {
          path: "/auth/verify-totp",
          element: <VerifyTotp />,
        },
        {
          path: "/auth/verify-sms",
          element: <VerifySMS />,
        },
        {
          path: "/auth/verify-email",
          element: <VerifyEmail />,
        },
        {
          path: "/auth/select-mfa",
          element: <SelectMfa />,
        },
        {
          path: "/auth/forgot-password",
          element: <ForgotPassword />,
        },
        {
          path: "/auth/new-password",
          element: <SetNewPassword />,
        },
      ],
    },
    {
      errorElement: <ErrorBoundary />,
      element: (
        <AuthenticatedGuard>
          <OrganisationProvider>
            <VidyoLayout />
          </OrganisationProvider>
        </AuthenticatedGuard>
      ),
      children: [
        {
          path: "/r/:key/call",
          element: <CallPage />,
        },
        {
          path: "/r/:key",
          element: (
            <PreCallLayout>
              <PreCall />
            </PreCallLayout>
          ),
        },
        {
          path: "/r/:key/after-call",
          element: (
            <PreCallLayout>
              <AfterCall />
            </PreCallLayout>
          ),
        },
      ],
    },
    {
      errorElement: <ErrorBoundary />,
      children: [
        {
          path: "/shared-recordings/:id",
          element: <SharedRecordingPage />,
        },
      ],
    },
    {
      path: "*",
      element: <NotFound />,
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_skipActionErrorRevalidation: true,
      v7_partialHydration: true,
    },
  },
);

export { router };
